import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { API } from 'api';
import { EvacZone } from 'shared/types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useCacheState } from 'state/useCacheState';
import { ENV } from 'shared/ENV';

type UseRegionEvacZonesGeoEvent = {
  regionEvacZones: EvacZone[];
};

const fetchEvacZonesForRegions = (
  regionIds: number[],
  cacheBusterTs: number | null,
): Promise<AxiosResponse<EvacZone[]>> => {
  const params = {
    regionIds: regionIds.toString(),
    showPending: true,
    ...(cacheBusterTs ? { ts: cacheBusterTs } : {}),
  };
  return API.get<EvacZone[]>('evac_zones/', { params });
};

export const useRegionEvacZonesForGeoEvent = (
  geoEvent?: Wildfire,
): UseRegionEvacZonesGeoEvent => {
  const { cacheBusterTs } = useCacheState();
  const regionIds =
    (geoEvent && geoEvent.regions.map((region) => region.id)) || [];
  const enabled = Boolean(geoEvent && regionIds.length);

  const query = useQuery({
    queryKey: ['evacuation-zones', geoEvent?.id, cacheBusterTs],
    queryFn: () => fetchEvacZonesForRegions(regionIds, cacheBusterTs),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 6, // 6 minutes - must be bigger than staleTime
    // In RTL tests, components frequently re-render.
    // Setting `refetchOnMount: 'always'` would require excessive `nock` mocks
    // or disabling missing interceptor errors. Opting against 'always' in tests.
    refetchOnMount: ENV.VITE_ENV === 'test' ? true : 'always', // Overrides stale data behavior to always refetch on mount.
  });

  const regionEvacZones = useMemo(
    () => (query.data?.data ?? []) as EvacZone[],
    [query.data?.data],
  );

  return { regionEvacZones };
};
